import {
  Checkbox,
  Divider,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { RoleAccordionBody } from '@/components/permissions/scopeAccordion';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';

import type { UpsertApiCredentialsState } from './upsertApiCredentialsFlow';

import { RenameEntityName } from '../wizard/renameEntityName';

const STUB_PROJECT = 'STUB_PROJECT';

export const ApiCredentialsScopesSelection = (
  props: StepProps<UpsertApiCredentialsState>
) => {
  const { rolesMap } = useRoles();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const dal = useDAL();
  const { data: projectsBody } = dal.projects.list();

  return (
    <>
      <StepContainer
        sx={{ padding: 0 }}
        navigationComponent={
          <RenameEntityName
            label='Credentials name'
            name={props.wizardState.name}
            onSubmit={(name) =>
              props.setWizardState((state) => ({ ...state, name }))
            }
          />
        }
        hideStepper
        onBackClick={props.back}
        backButtonText='Cancel'
        canGoNext={() => {
          if (
            !props.wizardState.roleId ||
            !rolesMap[props.wizardState.roleId]
          ) {
            return false;
          }

          if (
            rolesMap[props.wizardState.roleId].isProjectRole &&
            props.wizardState.projects.length === 0
          ) {
            return false;
          }
          return true;
        }}
        nextButtonText={
          props.wizardState.clientId ? 'Save Changes' : 'Create Credentials'
        }
        onNextClick={async () => {
          const updateEntity = { ...props.wizardState };
          if (!rolesMap[updateEntity.roleId].isProjectRole) {
            updateEntity.projects = [];
          }

          if (updateEntity.clientId) {
            await dal.apiCredentials.update(
              updateEntity.clientId,
              updateEntity
            );
            await props.finish();
          } else {
            const x = await dal.apiCredentials.create(updateEntity);
            props.setWizardState((state) => ({
              ...state,
              clientId: x.apiCredentialsProperties.clientId,
              clientSecret: x.clientSecret,
            }));
            props.setNextStep(props.currentStep.next?.[0]);
          }
        }}
      >
        <Typography
          fontWeight={300}
          className='ml-[100px] mt-[24px] mb-[40px] w-[700px]'
        >{`When code uses your API credentials to call the Eon API, it will have this role's permissions. Keep your account secure by choosing a role with just the permissions needed.`}</Typography>
        <Stack gap='24px' color={'var(--mui-palette-text-primary)'}>
          <Tabs
            onChange={(e, i) => setSelectedIndex(i)}
            value={selectedIndex}
            className='px-[140px]'
            sx={{
              marginX: '-40px',

              '.MuiTabs-scroller': { display: 'flex' },
            }}
          >
            <Tab
              label='Global roles'
              className='text-[14px] font-normal'
              disableRipple
            />
            <Tab
              label='Project roles'
              className='text-[14px] font-normal'
              disableRipple
            />
          </Tabs>
          {selectedIndex === 0 && (
            <Stack gap='12px' className='ml-[100px]'>
              <Typography variant='body1' className='mb-[24px] mt-[12px]'>
                Edit the permissions this credentials has by assigning global
                roles
              </Typography>
              <RoleAccordionBody
                roleToProjectsMappings={
                  props.wizardState.roleId
                    ? {
                        [props.wizardState.roleId]: {
                          ids: [STUB_PROJECT],
                        },
                      }
                    : {}
                }
                onChange={(roleToProjectsMappings) =>
                  props.setWizardState((state) => ({
                    ...state,
                    roleId: Object.keys(roleToProjectsMappings)[0],
                  }))
                }
                singleSelect
              />
            </Stack>
          )}
          {selectedIndex === 1 && (
            <Stack gap='12px' className='ml-[100px] mt-[12px]'>
              {projectsBody?.projects && projectsBody.projects.length > 1 && (
                <>
                  <Typography className='font-semibold'>Projects</Typography>
                  <Typography className='max-w-[580px]'>
                    Choose the projects you want the credentials to access
                  </Typography>
                  <Stack direction='row' gap='24px'>
                    {projectsBody?.projects.map((project) => (
                      <FormControlLabel
                        className='m-0'
                        key={project.id}
                        control={
                          <Checkbox
                            checked={props.wizardState.projects.includes(
                              project.id
                            )}
                            onChange={(event, checked) =>
                              props.setWizardState((state) => ({
                                ...state,
                                projects: checked
                                  ? [...state.projects, project.id]
                                  : state.projects.filter(
                                      (x) => x !== project.id
                                    ),
                              }))
                            }
                          />
                        }
                        label={
                          <Typography className='ml-[4px]'>
                            {project.name}
                          </Typography>
                        }
                      />
                    ))}
                  </Stack>
                  <Divider sx={{ mx: '-100px', my: '24px' }} />
                </>
              )}
              <RoleAccordionBody
                roleToProjectsMappings={
                  props.wizardState.roleId
                    ? {
                        [props.wizardState.roleId]: {
                          ids: [STUB_PROJECT],
                        },
                      }
                    : {}
                }
                onChange={(roleToProjectsMappings) =>
                  props.setWizardState((state) => ({
                    ...state,
                    roleId: Object.keys(roleToProjectsMappings)[0],
                  }))
                }
                projectId={STUB_PROJECT}
                singleSelect
              />
            </Stack>
          )}
        </Stack>
      </StepContainer>
    </>
  );
};
